import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { getLabelProperties } from './LabelEtiquette';
import { isPasswordValid } from './PasswordProtect';
import { resolveMediaUrl, getDemoPath } from '../utils/media';

/**
 * ProductCard — shared card used by ProductList, CategoryPage and VisualCatalogPage.
 *
 * Props:
 *  visuels      array of config_carte-style objects { visuel, url?, medium? }
 *  title        string
 *  price        number (euros, e.g. 2.50)
 *  badge        string | null
 *  linkTo       string | null
 *  editPath     string | null   — admin only: opens product edit in new tab
 *  children     ReactNode
 */
export default function ProductCard({ visuels = [], title, price, badge, linkTo, editPath, children }) {
  const isAdmin = isPasswordValid();
  const cardWrapRef = useRef(null);

  useGSAP((context, contextSafe) => {
    const el = cardWrapRef.current;
    const onEnter = contextSafe(() => gsap.to(el, { y: -7, duration: 0.28, ease: 'power2.out' }));
    const onLeave = contextSafe(() => gsap.to(el, { y: 0, duration: 0.4, ease: 'power2.inOut' }));
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, { scope: cardWrapRef });
  // Deux formes possibles pour un item de `visuels` :
  //  - { visuel, url, medium } venant de config_carte (galerie du produit) : `url` pointe
  //    vers le FOND BRUT (/assets_react/visuels/...) et ne doit JAMAIS être affiché tel
  //    quel — seule la démo dérivée de `visuel` (smartphone + zone à gratter) est montrée.
  //  - { url } sans `visuel`, construit uniquement quand le produit n'a AUCUN visuel
  //    rattaché (repli sur `featured_image`, une image de démo/lifestyle historique déjà
  //    prête à l'affichage, pas un fond) : `url` est alors la seule source valide.
  const candidates = visuels
    .slice(0, 4)
    .map((v) => [v.visuel ? getDemoPath(v.visuel) : v.url].filter(Boolean))
    .filter((list) => list.length > 0);

  const [attempts, setAttempts] = useState({});
  const handleError = (idx) => setAttempts((prev) => ({ ...prev, [idx]: (prev[idx] ?? 0) + 1 }));
  // null = toutes les candidates ont échoué → placeholder
  const effectiveImages = candidates.map((list, i) => {
    const a = attempts[i] ?? 0;
    return a < list.length ? list[a] : null;
  });

  const imgElement = (src, idx) => (
    <Box key={idx} sx={{ width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          aspectRatio: '1',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #fdf0ee 0%, #ece8f7 100%)',
        }}
      >
        {src ? (
          <img
            src={resolveMediaUrl(src)}
            alt={title}
            loading="lazy"
            onError={() => handleError(idx)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography sx={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#c48f8b', fontSize: '1.1rem', px: 2, textAlign: 'center' }}>
            {title}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const imageBlock = effectiveImages.length > 1 ? (
    <Carousel
      showThumbs={false}
      showArrows={false}
      infiniteLoop
      autoPlay
      interval={Math.floor(Math.random() * 1000 + 2000)}
      stopOnHover={false}
      swipeable={false}
      transitionTime={800}
      showStatus={false}
    >
      {effectiveImages.map((src, i) => imgElement(src, i))}
    </Carousel>
  ) : effectiveImages.length === 1 ? (
    imgElement(effectiveImages[0], 0)
  ) : null;

  return (
    <div ref={cardWrapRef} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Card
        sx={{
          position: 'relative',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(196,143,139,0.10)',
          border: '1px solid rgba(232,213,209,0.5)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 16px 48px rgba(196,143,139,0.22)',
          },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fefcfa',
        }}
      >
        {linkTo ? <Link to={linkTo} style={{ textDecoration: 'none' }}>{imageBlock}</Link> : imageBlock}

        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, pt: 2 }}>
          <Typography
            gutterBottom
            variant="h5"
            component="h3"
            sx={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontWeight: 600,
              fontSize: { xs: '1.15rem', md: '1.25rem' },
              color: '#3a2b2b',
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          {price != null && (
            <Typography
              className="prix"
              variant="body1"
              sx={{
                fontWeight: 700,
                color: '#8C8BC4',
                fontSize: '1.1rem',
              }}
            >
              {Number(price).toFixed(2).replace('.', ',')} € TTC
            </Typography>
          )}
          {children}
        </CardContent>

        {badge && (() => {
          const { backgroundColor, icon } = getLabelProperties(badge);
          return (
            <Chip
              label={badge}
              style={{ backgroundColor, color: '#FFFFFF' }}
              icon={icon}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontWeight: 600,
                fontSize: '0.72rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            />
          );
        })()}

        {isAdmin && editPath && (
          <Tooltip title="Éditer ce produit (nouvel onglet)">
            <IconButton
              component="a"
              href={editPath}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                bgcolor: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(4px)',
                '&:hover': { bgcolor: 'white' },
                zIndex: 10,
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Card>
    </div>
  );
}
